import { AfterContentChecked, Component, Inject, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { FormBuilder, NgControl, Validators } from '@angular/forms';
import { IAngularMyDpOptions } from '@nodro7/angular-mydatepicker';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { Age, AgeService } from '../../../common/age.service';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { DATEPICKER_CONFIG } from '../../../common/datepicker-config';
import { FingerabdruckService } from '../../../common/fingerabdruck.service';
import { ValidationService } from '../../../common/validation.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { AppConfig } from '../../../models/config/app-config';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';
import { BiometrieFingerprintStateService } from '../../../common/biometrie-fingerprint-state.service';

@Component({
  selector: 'app-fingerabdruck',
  templateUrl: './fingerabdruck.component.html',
  styleUrls: ['./fingerabdruck.component.scss']
})
export class FingerabdruckComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy, AfterContentChecked {
  private fingerabdruckListItemsSub: Subscription;
  private erfassungListItemsSub: Subscription;

  fingerabdruckListItems: ValueListItem[] = [];
  fingerabdruckListItemsFiltered: ValueListItem[] = [];  // a filterd list of fingerabdruckListItems
  erfassungListItems: ValueListItem[] = [];
  hasDevices = false;

  private _antragstellerAlter: { maxValue: number };
  private _antragstellerGeburtsdatum: string;
  private _antragstellerGeschlecht: string;
  private readonly AUSNAHME_FINGERABDRUECKE_BEREITS_ERFASST = 'FABE';
  private FA_AUSNAHMEGRUND_UNTER_12_JAHRE_LANGFRISTIGER_AUFENTHALT = 'KDAG';
  private FA_AUSNAHMEGRUND_UNTER_12_JAHRE_KURZFRISTIGER_AUFENTHALT = 'KDVG';

  private biometrieStateSub?: Subscription;
  showBiometrieMessage = false;
  private biometrieTooOld = false;

  @Input()
  set antragstellerAlter(antragstellerAlter: Age) {
    if (antragstellerAlter === this._antragstellerAlter) {
      // Es gab keine Änderung am Wert, also müssen wir nichts weiter tun
      return;
    }

    this._antragstellerAlter = antragstellerAlter;

    // Grundsätzlich müssen wir bei jeder Änderung von Geburtsdatum oder Geschlecht eventuell bereits
    // erfasste Fingerabdruckdaten löschen, da diese beiden Werte in den Metadaten der Fingerabdrücke enthalten sind
    this.setInputDependentState();
  }

  @Input()
  set antragstellerGeburtsdatum(antragstellerGeburtsdatum: string) {
    if (antragstellerGeburtsdatum === this._antragstellerGeburtsdatum) {
      // Es gab keine Änderung am Wert, also müssen wir nichts weiter tun
      return;
    }

    this._antragstellerGeburtsdatum = antragstellerGeburtsdatum;
  }

  @Input()
  set antragstellerGeschlecht(antragstellerGeschlecht: string) {
    if (antragstellerGeschlecht === this._antragstellerGeschlecht) {
      // Es gab keine Änderung am Wert, also müssen wir nichts weiter tun
      return;
    }

    this._antragstellerGeschlecht = antragstellerGeschlecht;

    // Grundsätzlich müssen wir bei jeder Änderung von Geburtsdatum oder Geschlecht eventuell bereits
    // erfasste Fingerabdruckdaten löschen, da diese beiden Werte in den Metadaten der Fingerabdrücke enthalten sind
    this.deleteFingerprintsIfCaptured();
  }

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              private validationService: ValidationService,
              private fingerabdruckService: FingerabdruckService,
              private ageService: AgeService,
              private biometrieFpState: BiometrieFingerprintStateService,
              assistantPopoverService: AssistantPopoverService,
              @Inject(DATEPICKER_CONFIG) public datepickerOptions: IAngularMyDpOptions,
              @Optional() @Self() controlDir: NgControl,
              @Inject(APP_CONFIG) public appConfig: AppConfig) {
    super(controlDir, assistantPopoverService);

    this.form = this.wrapFormGroup(fb.group({
      erforderlich: 'ja',
      scannertyp: null,
      ausnahmegrund: [null],
      fingerabdruecke: [null, [Validators.required]],
      wasCapturingSuccessful: null
    }));
  }

  protected isVidex(): boolean {
    return this.appConfig?.umgebung === 'VIDEX';
  }

  private ausnahmegrundSub?: Subscription;

  ngOnInit() {
    super.ngOnInit();

    this.fingerabdruckListItemsSub = this.valueListsService.getValueListItems('fingerabdruecke_ausnahmegrund')
      .subscribe((listItems: ValueListItem[]) => {
        this.fingerabdruckListItems = listItems;
        this.updateAusnahmegrundOptions();
      });

    this.erfassungListItemsSub = this.fingerabdruckService.getFingerabdruckDevices()
    .subscribe((listItems: ValueListItem[]) => {
      this.erfassungListItems = listItems;
      this.hasDevices = this.erfassungListItems.length > 0;
    });

    this.form.controls.erforderlich.valueChanges.subscribe(value => {
      if (value !== this.form.value.erforderlich) {
        this.erforderlichValueChanged(value);
      }
      this.updateBiometrieMessageVisibility();
      this.updateAusnahmegrundOptions();
    });

    // biometre state
    this.biometrieStateSub = this.biometrieFpState.biometrieDateOlderThanMaxMonths$
      .subscribe((tooOld: boolean) => {
        this.biometrieTooOld = tooOld;
        this.updateBiometrieMessageVisibility();
        this.updateAusnahmegrundOptions();
      });

    this.ausnahmegrundSub = this.form.controls.ausnahmegrund.valueChanges.subscribe(() => {
      this.updateBiometrieMessageVisibility();
    });

    this.updateBiometrieMessageVisibility();
    this.updateAusnahmegrundOptions();

    this.setInputDependentState();
  }

  private updateBiometrieMessageVisibility() {
    const ausnahmegrund = this.form.controls.ausnahmegrund.value;

    this.showBiometrieMessage =
      !this.isVidex()
      && this.biometrieTooOld
      && this.form.controls.erforderlich.value === 'nein'
      && (ausnahmegrund == null || ausnahmegrund === '');
  }


  private updateAusnahmegrundOptions() {
    if (this.isVidex()) {
      this.fingerabdruckListItemsFiltered = [...this.fingerabdruckListItems];
      return;
    }

    const shouldHide = this.biometrieTooOld && this.form.controls.erforderlich.value === 'nein';

    this.fingerabdruckListItemsFiltered = shouldHide
      ? this.fingerabdruckListItems.filter(item => item.key !== this.AUSNAHME_FINGERABDRUECKE_BEREITS_ERFASST)
      : [...this.fingerabdruckListItems];

    const current = this.form.controls.ausnahmegrund.value;
    const stillAllowed = this.fingerabdruckListItemsFiltered.some(i => i.key === current);

    if (!stillAllowed) {
      this.form.controls.ausnahmegrund.setValue(null);
      this.form.controls.ausnahmegrund.markAsTouched();
      this.form.controls.ausnahmegrund.updateValueAndValidity();
    }
  }

  ngAfterContentChecked(): void {
    // Wird nach jeder Model-Änderung aber noch vor Initialisierung/Aktualisierung der View aufgerufen
    // (siehe https://angular.io/guide/lifecycle-hooks). Die Wertelisten sind zu diesem Zeitpunkt bereits
    // geladen und die Select-Options gefüllt und damit für eine initiale Vorbelegung verfügbar.
    if (!this.form.value.scannertyp) {
      const initialDevice = this.fingerabdruckService.findInitialDevice();
      if (initialDevice) {
        this.form.controls['scannertyp'].setValue(initialDevice);
      }
    }
  }

  ngOnDestroy(): void {
    this.ausnahmegrundSub?.unsubscribe();
    this.fingerabdruckListItemsSub.unsubscribe();
    this.erfassungListItemsSub.unsubscribe();
    this.biometrieStateSub?.unsubscribe();
  }

  onValidatorUpdate() {
    this.setInputDependentState();
  }

  /**
   * Formularzustand abhängig von gesetzten Eingaben setzen
   */
  setInputDependentState() {
    const value: string = this.isAntragstellerYoungerThan12() ? 'nein' : 'ja';
    this.form.controls.erforderlich.setValue(value);
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
  }

  /**
   * Gibt <code>true</code> zurück, falls der Antragsteller jünger als 12 Jahre ist, ansonsten <code>false</code>.
   */
  isAntragstellerYoungerThan12(): boolean {
    return this._antragstellerAlter && this._antragstellerAlter.maxValue < 12;
  }

  captureFingerabdruck() {
    const form = this.form;
    this.fingerabdruckService.captureFingerabdruecke(this.form.value.scannertyp, this._antragstellerGeschlecht, this._antragstellerGeburtsdatum)
    .subscribe(
      {
        next: (blob) => {
          const fileReader = new FileReader();
          fileReader.onload = () => {
            form.patchValue({ fingerabdruecke: btoa(fileReader.result as string), wasCapturingSuccessful: true });
            this.form.controls.fingerabdruecke.markAsTouched();
            this.form.updateValueAndValidity();
          };
          fileReader.readAsBinaryString(blob);
        },
        error: () => {
          form.patchValue({ fingerabdruecke: null, wasCapturingSuccessful: false });
          this.form.controls.fingerabdruecke.markAsTouched();
        }
      });
  }

  isCaptureButtonDisabled() {
    return !(this._antragstellerGeburtsdatum && this._antragstellerGeschlecht && this.form.value.scannertyp);
  }

  /**
   * Löscht eventuell schon erfasste Fingerabdrücke.
   */
  deleteFingerprintsIfCaptured() {
    this.form.patchValue({ fingerabdruecke: undefined });
    this.form.controls['wasCapturingSuccessful'].setValue(undefined);
  }

  changeDevice(): void {
    this.fingerabdruckService.changeLastUsedDevice(this.form.value.scannertyp);
  }

  private erforderlichValueChanged(erforderlich: string) {
    this.deleteFingerprintsIfCaptured();
    const fingerabdrueckeErforderlich: boolean = (erforderlich === 'ja');
    this.updateValidators(fingerabdrueckeErforderlich);
    this.updateAusnahmegrund(fingerabdrueckeErforderlich);
    this.form.controls.fingerabdruecke.updateValueAndValidity();
    this.form.controls.ausnahmegrund.updateValueAndValidity();
  }

  private updateAusnahmegrund(fingerabdrueckeErforderlich: boolean) {
    const ausnahmegrundErforderlich: boolean = !fingerabdrueckeErforderlich && this.isAntragstellerYoungerThan12();
    const value: string = ausnahmegrundErforderlich ? this.getAusnamegrund() : null;
    this.form.controls.ausnahmegrund.setValue(value);
  }

  private updateValidators(fingerabdrueckeErforderlich: boolean) {
    this.form.controls.fingerabdruecke.setValidators(fingerabdrueckeErforderlich ? Validators.required : null);
    this.form.controls.ausnahmegrund.setValidators(fingerabdrueckeErforderlich ? null : Validators.required);
  }

  private getAusnamegrund(): string {
    if (this.appConfig.visumAntragsartEnum === 'LANGFRISTIGER_AUFENTHALT') {
      const autoAusnahme: string = this.getAutoAusnahme();
      return autoAusnahme != null ? autoAusnahme :
        this.FA_AUSNAHMEGRUND_UNTER_12_JAHRE_LANGFRISTIGER_AUFENTHALT;
    } else {
      return this.FA_AUSNAHMEGRUND_UNTER_12_JAHRE_KURZFRISTIGER_AUFENTHALT;
    }
  }

  private getAutoAusnahme(): string {
    const entry = this.fingerabdruckListItems.find(item => item.fields?.autoAusnahmegrund === true);
    return entry ? entry.key : null;
  }
}
